import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  Chip,
  Typography,
  Tooltip,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import zoneService from '../services/zoneService';
import { Zone } from '../types/zone';
import { useAuth } from '../contexts/AuthContext';

const ZoneListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchName, setSearchName] = useState('');
  const [showHidden, setShowHidden] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const fetchZones = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await zoneService.getZones({
        page: page + 1,
        limit: rowsPerPage,
        name: searchName || undefined,
        is_visible: showHidden ? undefined : true,
      });
      setZones(response.data.zones);
      setTotal(response.data.pagination.total);
    } catch (err) {
      setError(t('zones.errors.fetchFailed'));
      console.error('Error fetching zones:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchName, showHidden, t]);

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  const handleToggleVisibility = async (zone: Zone) => {
    try {
      await zoneService.toggleVisibility(zone.id, !zone.is_visible);
      fetchZones();
    } catch (err) {
      setError(t('zones.errors.toggleFailed'));
      console.error('Error toggling visibility:', err);
    }
  };

  const handleDelete = async (zone: Zone) => {
    if (!window.confirm(t('zones.confirmDelete', { name: zone.name }))) {
      return;
    }

    try {
      await zoneService.deleteZone(zone.id);
      fetchZones();
    } catch (err) {
      setError(t('zones.errors.deleteFailed'));
      console.error('Error deleting zone:', err);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('zones.title')}</Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/zones/new')}
          >
            {t('zones.createNew')}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            label={t('zones.searchByName')}
            variant="outlined"
            size="small"
            value={searchName}
            onChange={(e) => {
              setSearchName(e.target.value);
              setPage(0);
            }}
            sx={{ flexGrow: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showHidden}
                onChange={(e) => {
                  setShowHidden(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label={t('zones.showHidden')}
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('zones.table.name')}</TableCell>
              <TableCell>{t('zones.table.location')}</TableCell>
              <TableCell>{t('zones.table.radius')}</TableCell>
              <TableCell>{t('zones.table.color')}</TableCell>
              <TableCell>{t('zones.table.ppCount')}</TableCell>
              <TableCell>{t('zones.table.visibility')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : zones.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">{t('zones.noZones')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              zones.map((zone) => (
                <TableRow key={zone.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {zone.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {zone.center_latitude.toFixed(6)}, {zone.center_longitude.toFixed(6)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {(zone.radius_meters / 1000).toFixed(1)} km
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1,
                          backgroundColor: zone.color,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {zone.color}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={(zone as Zone & { cpeCount?: number }).cpeCount || 0}
                      size="small"
                      color={(zone as Zone & { cpeCount?: number }).cpeCount! > 0 ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {isAdmin ? (
                      <IconButton
                        size="small"
                        onClick={() => handleToggleVisibility(zone)}
                        color={zone.is_visible ? 'primary' : 'default'}
                      >
                        {zone.is_visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    ) : (
                      <Chip
                        icon={zone.is_visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        label={zone.is_visible ? t('common.visible') : t('common.hidden')}
                        size="small"
                        color={zone.is_visible ? 'success' : 'default'}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('common.view')}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/zones/${zone.id}`)}
                      >
                        <LocationIcon />
                      </IconButton>
                    </Tooltip>
                    {isAdmin && (
                      <>
                        <Tooltip title={t('common.edit')}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/zones/${zone.id}/edit`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.delete')}>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(zone)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[20, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default ZoneListPage;

